import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { MwCorePreferenceService, MwUserService } from '@core/auth';
import { MwPerfectScrollBarDirective } from '@core/directives/perfect-scrollbar.directive';
import { MW_LAYOUT_HIDE_NAVBAR } from '@core/injection-tokens';
import { MwAppConfigService, MwIconLoaderService } from '@core/services';
import { Config } from '@core/config';

import { MwSidebarComponent } from './@components/sidebar/sidebar.component';
import { SidebarId } from './layout.constants';
import { LayoutOptions, MW_LAYOUT_OPTIONS } from './layout.options';
import { MwLayoutService } from './layout.service';

@Component({
  selector: 'mw-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['layout.component.scss'],
})
export class MwLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  config: Config;
  navBarFolded = false;
  rightNavBarFolded = true;
  @ViewChild('navSidebarRef') navSidebar: MwSidebarComponent;
  @ViewChild('navSidebarRef', { read: MwPerfectScrollBarDirective })
  perfectScrollNav: MwPerfectScrollBarDirective;
  @ViewChild('layoutScrollBar', { read: MwPerfectScrollBarDirective })
  layoutScrollBar: MwPerfectScrollBarDirective;
  hideNavBar = false;
  emptyList = false;

  get noPaddings(): boolean {
    return this.config?.layout?.noPaddings ?? false;
  }

  get navBarFoldedWidth(): number {
    return this.config?.layout?.navbar?.foldedWidth;
  }

  get fullWidth(): boolean {
    return this.config?.layout?.fullWidth;
  }

  get showNewsButton(): boolean {
    return this.config?.layout?.toolbar?.showNewsButton ?? true;
  }

  get showModuleButton(): boolean {
    return this.config?.layout?.toolbar?.showModulesButton ?? true;
  }

  showToggler = true;
  showCompactLogo = false;
  showFullScreenButton = true;
  SidebarId = SidebarId;

  userHasAccessToModule$ = this.userService.userProfile$.pipe(
    filter((profile) => !!profile),
    map((profile) => profile.hasAccessToModule)
  );

  private unsubscribeAll$ = new Subject<void>();
  private icons = ['mw-icon-info'];

  constructor(
    private readonly configService: MwAppConfigService,
    private readonly iconsLoader: MwIconLoaderService,
    private readonly preferenceService: MwCorePreferenceService,
    private readonly router: Router,
    private readonly layoutService: MwLayoutService,
    private readonly userService: MwUserService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly injector: Injector,

    @Optional()
    @Inject(MW_LAYOUT_OPTIONS)
    readonly options?: LayoutOptions
  ) {
    this.hideNavBar = this.injector.get<boolean>(MW_LAYOUT_HIDE_NAVBAR, false);
    this.iconsLoader.load(this.icons);
    this.navBarFolded = this.preferenceService.getValue('navSidebarFolded');

    if (this.navBarFolded === null) {
      this.navBarFolded = false;
    }
  }

  navItemToggled(): void {
    setTimeout(() => this.perfectScrollNav.update(), 150);
  }

  ngOnInit(): void {
    this.subscribeToConfig();
    this.subscribeToNavbarItemsChange();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  ngAfterViewInit(): void {
    this.subscribeToNavigationEnd();
    this.setupLayoutServiceAfterViewInit();
  }

  onNavSidebarFoldChanged(folded: boolean): void {
    this.navBarFolded = folded;
    this.preferenceService.setValue('navSidebarFolded', folded);
  }

  private onConfigChange(config: Config): void {
    this.config = config;
    const isNavbarFolded = config?.layout?.navbar?.folded === true;

    if (isNavbarFolded !== this.navBarFolded && this.navSidebar) {
      this.navSidebar.toggleFold();
    }
    this.showToggler =
      (config?.layout?.toolbar?.showToggler ?? this.showToggler) &&
      !this.hideNavBar;
    this.showCompactLogo =
      config?.layout?.toolbar?.showCompactLogo ?? this.showCompactLogo;
    this.showFullScreenButton =
      config?.layout?.toolbar?.showFullscreenButton ??
      this.showFullScreenButton;
    this.changeDetectorRef.detectChanges();
  }

  private setupLayoutServiceAfterViewInit(): void {
    this.layoutService.setLayoutScrollBar(this.layoutScrollBar);
  }

  private subscribeToNavigationEnd(): void {
    this.router.events
      .pipe(
        takeUntil(this.unsubscribeAll$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => this.layoutScrollBar.scrollToTop());
  }

  private subscribeToConfig(): void {
    this.configService
      .getConfig()
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((config: Config) => this.onConfigChange(config));
  }

  private subscribeToNavbarItemsChange(): void {
    this.layoutService.changeNavBarItems$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((items) => (this.emptyList = items.length === 0));
  }
}
